/**
 * The public site's per-route metadata, in one place.
 *
 * Read twice, deliberately:
 *
 *  - at runtime by `usePageMeta`, which sets the tags once React mounts; and
 *  - at BUILD time by the `emitMarketingHtml` plugin in vite.config.ts, which
 *    writes one static HTML file per route with these values baked into the
 *    served markup.
 *
 * The build half is the one that matters for sharing. weavein.ai is a
 * client-rendered SPA served from a single index.html, so a link unfurler —
 * Slack, LinkedIn, iMessage, most crawlers — reads that one file and never
 * runs the JavaScript that would correct it. Every shared link therefore
 * previewed identically, with the one title baked into index.html.
 *
 * This is the same trick `emitTrivenyHtml` already uses for the Triveny apex,
 * generalised to the marketing routes.
 *
 * One table rather than two so the tags a crawler sees and the tags a browser
 * ends up with cannot drift apart.
 */
export interface RouteMeta {
  title: string;
  description: string;
  /** Root-relative or absolute; falls back to the site default. */
  image?: string;
}

export const SITE_ORIGIN = 'https://weavein.ai';
export const DEFAULT_OG_IMAGE = '/images/hero-family.jpg';

export const SEO_ROUTES: Record<string, RouteMeta> = {
  '/': {
    title: 'WeaveIn | One ledger. Every funder.',
    description:
      'WeaveIn sits between community organizations and the plans, counties and grants that pay them. Capture the visit once; agents handle each funder\u2019s eligibility, submission and payment matching, and a named person approves every step.',
  },
  '/platform': {
    title: 'Platform | WeaveIn',
    description:
      'Six capabilities behind every funder: the trust layer, eligibility, encounter capture, funder rules and submission, disbursement and reconciliation. Agents draft; people approve.',
  },
  '/funder-coverage': {
    title: 'Funder Coverage | WeaveIn',
    description:
      'Plan rules held as data for California\u2019s Medi-Cal plans, and every U.S. payer on a national clearinghouse network reachable for eligibility, claims and remittance. Adding a funder is configuration, not a release, and the opportunity finder shows the funders you are not billing yet.',
  },
  '/impact': {
    title: 'Impact | WeaveIn',
    description:
      'Medi-Cal outreach across four CBO federations: 19.25% engagement, 12.8\u00d7 the CHCS benchmark, measured against published baselines.',
  },
  '/solutions/hubs': {
    title: 'For Community Organizations & Hubs | WeaveIn',
    description:
      'You delivered the care. Capture the visit once, in the field, and get paid by every funder it qualifies for \u2014 plans, counties and grants.',
  },
  '/solutions/providers': {
    title: 'For Hospitals & Health Systems | WeaveIn',
    description:
      'Send a patient to real help in the community and find out whether they actually received it, without leaving your workflow.',
  },
  '/solutions/health-plans': {
    title: 'For Health Plans & Counties | WeaveIn',
    description:
      'Know which organization delivered which service to which member, on what date \u2014 as a record, not an estimate. Reach members through organizations they already trust.',
  },
  '/partners': {
    title: 'For Partners & Developers | WeaveIn',
    description:
      'Every funder\u2019s interface behind one API and an embedded agent: eligibility, submission, disbursement and remittance, inside the product your population already uses.',
  },
  '/solutions/emergency-response': {
    title: 'Emergency Response | WeaveIn',
    description:
      'Reach members by geography during a wildfire, outbreak or evacuation, and verify who is safe and who is unreachable.',
  },
  '/developers': {
    title: 'Developers | WeaveIn',
    description:
      'Every funder\u2019s interface behind one call. Partner API v1, scoped API keys, signed webhooks, Postman collections and a sandbox.',
  },
  '/security': {
    title: 'Security | WeaveIn',
    description:
      'HIPAA, BAAs, SOC 2 in process, SAML SSO, tenant isolation, 42 CFR Part 2 consent, audit logging \u2014 and agents that draft while people approve.',
  },
  '/solutions/employers': {
    title: 'For Employers | WeaveIn',
    description:
      'Address the social drivers behind healthcare cost, absenteeism and turnover in your workforce.',
  },
  '/solutions/workforce-management': {
    title: 'Workforce Management | WeaveIn',
    description:
      'Certify, schedule and support community health workers, navigators and peer support specialists.',
  },
  '/company/about': {
    title: 'About WeaveIn | Trust',
    description:
      'We built the Plaid for commercial health insurance in 2010. WeaveIn is the same primitive for government benefits.',
  },
  '/contact': {
    title: 'Contact | WeaveIn',
    description:
      'Tell us what you are trying to connect \u2014 a program you cannot bill for, a funder you cannot reach, a population you cannot serve.',
  },
  '/company/team': {
    title: 'Our Team | WeaveIn',
    description: 'The leadership, medical advisors and board behind Trust and WeaveIn.',
  },
  '/resources': {
    title: 'Resources | WeaveIn',
    description:
      'Guides, webinars and tools for community organizations, health plans, partners and employers.',
  },
  '/roi-calculator': {
    title: 'ROI Calculator | WeaveIn',
    description:
      'Estimate the cost of unaddressed social needs in your workforce, using your own assumptions.',
  },
};

/** The slug a route's prerendered file is written to (`/` → `home`). */
export function seoFileName(route: string): string {
  return route === '/' ? 'home' : route.replace(/^\//, '').replace(/\//g, '-');
}
